import { type ReactElement, type ReactNode, useCallback, useEffect } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import LinkIcon from '@atlaskit/icon/core/link';
import PageIcon from '@atlaskit/icon/core/page';
import TagIcon from '@atlaskit/icon/core/tag';
import VideoIcon from '@atlaskit/icon/core/video';
import { LoomLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { JiraBackground } from '@atlassian/product-wireframes';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Modal } from '@/components/signup/layouts/modal';
import { Image } from '@/libraries/design/components/image/src/image';
import { Link } from '@/libraries/design/components/link/src/link';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import twcOthersLogos from './assets/twc-others.svg';
import { useChoiceRedirect } from './choice-urls';
import { messages } from './messages';
import {
	fireOptInChoiceChanged,
	fireOptInChoiceSelected,
	fireOptInModalViewed,
	type OptInEventContext,
} from './opt-in-analytics';
import { OptInGraphicPanel } from './opt-in-graphic-panel';
import { type ChoiceValue } from './types';
import { type TwcPriceMode } from './use-twc-price';

/**
 * Building blocks shared by every TWCG-2108 Loom Land opt-in variant: the modal
 * shell (50/50 content + graphic panel), analytics/selection hooks, and the
 * common header, feature list and legal copy. Variants only own what differs.
 */

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------

// Figma "TWC _ Variant A/B" frame height (px). Both columns share it so the
// 50/50 split reads as a full-height panel.
const MODAL_HEIGHT = 600;

// Content (left) + graphic panel (right), filling the full modal width so the
// panel bleeds to the modal's right edge. The "md" modal (972px) is ~the Figma
// 968px frame.
const layoutCss = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	// minHeight (not a hard height) lets content grow without a scrollbar.
	minHeight: `${MODAL_HEIGHT}px`,
	width: '100%',
});

// Figma "Content" / "Main Card" frames: 64px top, 48px sides. An explicit 50%
// basis (plus minWidth:0) keeps the cards' intrinsic width from widening it.
const contentCss = css({
	display: 'flex',
	flexDirection: 'column',
	paddingBlockStart: token('space.800'),
	paddingInline: token('space.600'),
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	boxSizing: 'border-box',
});

// Variant A: heading + cards at the top, legal text pinned to the bottom.
const choiceContentCss = css({
	justifyContent: 'space-between',
	gap: token('space.250'),
	paddingBlockEnd: token('space.600'),
});

// Variant B: stacked sections, with the actions pushed to the bottom.
const singleContentCss = css({
	gap: token('space.500'),
	paddingBlockEnd: token('space.800'),
});

export type OptInModalLayoutProps = {
	/** Test id for the left-hand content column. */
	contentTestId: string;
	/** Content column arrangement: "choice" (Variant A) or "single" (Variant B). */
	contentLayout: 'choice' | 'single';
	/** Illustration shown in the graphic panel. */
	activeIllustration: ChoiceValue;
	/** Every illustration the modal can switch between (preloaded). */
	illustrations?: ChoiceValue[];
	/** Override the Loom clip played over the graphic panel on load (e.g. Storybook). */
	previewVideoSrc?: string;
	children: ReactNode;
};

export const OptInModalLayout = ({
	contentTestId,
	contentLayout,
	activeIllustration,
	illustrations,
	previewVideoSrc,
	children,
}: OptInModalLayoutProps) => (
	<JiraBackground>
		<Modal
			size="md"
			productLogo={<LoomLogo appearance="brand" size="medium" shouldUseNewLogoDesign />}
		>
			<div css={layoutCss}>
				<div
					css={[contentCss, contentLayout === 'choice' ? choiceContentCss : singleContentCss]}
					data-testid={contentTestId}
				>
					{children}
				</div>
				<OptInGraphicPanel
					active={activeIllustration}
					illustrations={illustrations}
					videoSrc={previewVideoSrc}
				/>
			</div>
		</Modal>
	</JiraBackground>
);

// ---------------------------------------------------------------------------
// Analytics + selection
// ---------------------------------------------------------------------------

/**
 * Fires the modal "viewed" screen event once shown (see `opt-in-analytics`).
 * Guarded on the analytics client, and re-run when it arrives, so it fires once
 * the client is ready and no-ops outside the live signup flow (Storybook).
 */
export const useOptInModalViewed = ({ variant, cohort, edition, priceMode }: OptInEventContext) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();

	useEffect(() => {
		if (!analyticsClient) {
			return;
		}
		fireOptInModalViewed({
			createAnalyticsEvent,
			context: { variant, cohort, edition, priceMode },
		});
	}, [analyticsClient, createAnalyticsEvent, variant, cohort, edition, priceMode]);
};

/**
 * Returns a handler that fires the card-toggle event for Variant A. Memoized on
 * the context's primitive fields (not the object), so callers can pass an inline
 * context and still get a stable reference.
 */
export const useOptInChoiceChanged = ({
	variant,
	cohort,
	edition,
	priceMode,
}: OptInEventContext) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(choice: ChoiceValue) =>
			fireOptInChoiceChanged({
				createAnalyticsEvent,
				context: { variant, cohort, edition, priceMode },
				choice,
			}),
		[createAnalyticsEvent, variant, cohort, edition, priceMode],
	);
};

export type UseOptInSelectOptions = {
	/** Optional extra side effect run before the redirect. */
	onSelect?: (choice: ChoiceValue) => void;
	/** Analytics attributes; `edition` also picks the provisioned TWC edition. */
	context: OptInEventContext;
	/** Original page query string, passed through to the Loom-only signup redirect. */
	search: string;
};

/**
 * Returns the handler for a confirmed choice: fires the selection event, runs
 * the optional `onSelect` callback, then navigates to that choice's
 * signup/provisioning flow. Side effects run before the redirect so they are
 * sent before navigation. Shared so every variant confirms a choice the same way.
 *
 * Memoized on the context's primitive fields (not the object) so the handler
 * stays stable across renders; it changes only if `onSelect` itself changes.
 */
export const useOptInSelect = ({ onSelect, context, search }: UseOptInSelectOptions) => {
	const { variant, cohort, edition, priceMode } = context;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const redirectToChoice = useChoiceRedirect(edition, search);

	return useCallback(
		(choice: ChoiceValue) => {
			fireOptInChoiceSelected({
				createAnalyticsEvent,
				context: { variant, cohort, edition, priceMode },
				choice,
			});
			onSelect?.(choice);
			redirectToChoice(choice);
		},
		[createAnalyticsEvent, redirectToChoice, onSelect, variant, cohort, edition, priceMode],
	);
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

// `text-wrap: balance` keeps the title tidy if it ever wraps on narrow content.
const headingCss = css({
	textWrap: 'balance',
});

/** Figma "Heading/L" (24/28 bold) — DS Heading "large". */
export const OptInHeading = () => (
	<div css={headingCss}>
		<Heading size="large" as="h1">
			<FormattedMessage {...messages.heading} />
		</Heading>
	</div>
);

const subtitleCss = css({
	margin: 0,
	// Figma "Body/M Regular" (14/20).
	font: token('font.body'),
	color: token('color.text'),
});

export const OptInSubtitle = ({ message }: { message: MessageDescriptor }) => (
	<p css={subtitleCss}>
		<FormattedMessage {...message} />
	</p>
);

const logosCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	// Reset to regular weight: the DS Radio label applies a heavier weight, but
	// the header should read lighter (the Loom wordmark keeps its own styling).
	font: token('font.body'),
	fontWeight: token('font.weight.regular'),
});

const plusCss = css({
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
});

// Combined product-logos graphic (Jira, Confluence, Trello, Rovo), sized to
// match the Loom wordmark height beside it.
const appTilesCss = css({
	display: 'block',
	height: '30px',
	width: 'auto',
});

/** "Loom + [Jira, Confluence, Trello, Rovo]" logo row. */
export const TwcLogos = () => {
	const intl = useIntl();
	return (
		<span css={logosCss}>
			<LoomLogo appearance="brand" size="small" shouldUseNewLogoDesign />
			<span css={plusCss} aria-hidden="true">
				+
			</span>
			<Image
				css={appTilesCss}
				src={twcOthersLogos}
				alt={intl.formatMessage(messages.twcOtherAppsAlt)}
			/>
		</span>
	);
};

// Loom-only header: bottom-aligned so the "only" qualifier sits on the Loom
// wordmark's baseline rather than its vertical centre.
const loomOnlyLogosCss = css({
	alignItems: 'flex-end',
});

// The Loom "small" logo's glyphs sit around the centre of its ~24px box, so the
// text is anchored to the bottom and nudged to land on the visual baseline.
const onlyLabelCss = css({
	alignSelf: 'flex-end',
	lineHeight: 1,
	transform: 'translateY(-3px)',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
});

/** "Loom only" logo row. */
export const LoomOnlyLogo = () => (
	<span css={[logosCss, loomOnlyLogosCss]}>
		<LoomLogo appearance="brand" size="small" shouldUseNewLogoDesign />
		<span css={onlyLabelCss}>
			<FormattedMessage {...messages.loomOnlyLabel} />
		</span>
	</span>
);

// ---------------------------------------------------------------------------
// Feature list
// ---------------------------------------------------------------------------

// A feature row: the message descriptor plus its icon. `values` carries optional
// ICU arguments for messages that interpolate (e.g. the {price} pricing line).
export type Feature = {
	message: MessageDescriptor;
	icon: ReactElement;
	values?: Record<string, ReactNode>;
};

// Feature icons use the Atlassian brand blue to match Figma.
const featureIconColor = token('color.icon.brand');

const priceMessageForMode = (mode: TwcPriceMode): MessageDescriptor => {
	switch (mode) {
		case 'less':
			return messages.twcFeaturePriceLess;
		case 'absolute':
			return messages.twcFeaturePriceAbsolute;
		case 'more':
		default:
			return messages.twcFeaturePrice;
	}
};

export const buildTwcFeatures = (price: ReactNode, mode: TwcPriceMode = 'more'): Feature[] => [
	{
		message: messages.twcFeatureEmbed,
		icon: <VideoIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.twcFeatureNotes,
		icon: <PageIcon label="" color={featureIconColor} />,
	},
	{
		message: priceMessageForMode(mode),
		icon: <TagIcon label="" color={featureIconColor} />,
		values: { price },
	},
	{
		message: messages.twcFeatureTrial,
		icon: <CalendarIcon label="" color={featureIconColor} />,
	},
];

export const LOOM_ONLY_FEATURES: Feature[] = [
	{
		message: messages.loomOnlyFeatureRecord,
		icon: <VideoIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.loomOnlyFeatureRecaps,
		icon: <PageIcon label="" color={featureIconColor} />,
	},
	{
		message: messages.loomOnlyFeatureShare,
		icon: <LinkIcon label="" color={featureIconColor} />,
	},
];

const listCss = css({
	display: 'flex',
	flexDirection: 'column',
	margin: 0,
	padding: 0,
	listStyle: 'none',
});

const listItemCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

// Compact rows inside Variant A's cards (Figma "Body/S", 12/16), keeping long
// copy such as the pricing line on a single line.
const smallListCss = css({
	gap: token('space.150'),
	font: token('font.body.small'),
	color: token('color.text.subtle'),
});

// Roomier rows for Variant B (Figma "Body/M", 14/20, 36px cells, 4px gap).
const mediumListCss = css({
	gap: token('space.050'),
	paddingBlockEnd: token('space.075'),
	font: token('font.body'),
	color: token('color.text.subtle'),
});

const mediumListItemCss = css({
	minHeight: '36px',
});

// Keeps the icon from shrinking (which would otherwise force text to wrap).
const listIconCss = css({
	flexShrink: 0,
	display: 'flex',
});

const listTextCss = css({
	minWidth: 0,
});

export type FeatureListProps = {
	features: Feature[];
	/** "small" for Variant A's compact cards, "medium" for Variant B. */
	size?: 'small' | 'medium';
};

export const FeatureList = ({ features, size = 'small' }: FeatureListProps) => (
	<ul css={[listCss, size === 'small' ? smallListCss : mediumListCss]}>
		{features.map((feature) => (
			<li key={feature.message.id} css={[listItemCss, size === 'medium' && mediumListItemCss]}>
				<span css={listIconCss}>{feature.icon}</span>
				<span css={listTextCss}>
					<FormattedMessage {...feature.message} values={feature.values} />
				</span>
			</li>
		))}
	</ul>
);

// ---------------------------------------------------------------------------
// Legal
// ---------------------------------------------------------------------------

// Coerces `FormattedMessage` chunk children to a plain string. ICU passes the
// chunk content as an array; the messages here are always plain text, so a
// non-primitive `ReactNode` can never render as "[object Object]".
const chunksToString = (chunks: ReactNode): string =>
	(Array.isArray(chunks) ? chunks : [chunks]).map((c) => (typeof c === 'string' ? c : '')).join('');

// Inline legal links. Module-scoped (not created during render) so they don't
// create unstable component types and the a11y linter can see the anchor
// content. Hrefs are localized via `useLocalizedHref`.
export const CustomerAgreementLink = (chunks: ReactNode) => {
	const href = useLocalizedHref('/legal/atlassian-customer-agreement');
	const label = chunksToString(chunks);
	return <Link aria-label={label} href={href} text={label} type="inline" />;
};

export const PrivacyPolicyLink = (chunks: ReactNode) => {
	const href = useLocalizedHref('/legal/privacy-policy');
	const label = chunksToString(chunks);
	return <Link aria-label={label} href={href} text={label} type="inline" />;
};

export const CancelAnytimeLink = (chunks: ReactNode) => {
	const label = chunksToString(chunks);
	return (
		<Link
			aria-label={label}
			href="https://support.atlassian.com/subscriptions-and-billing/docs/cancel-a-subscription/"
			text={label}
			type="inline"
		/>
	);
};

const legalCss = css({
	margin: 0,
	// Figma legal disclaimer: "Body/S" (12/16).
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

/** Legal disclaimer with inline links; the message varies by chosen CTA. */
export const LegalText = ({ message }: { message: MessageDescriptor }) => (
	<p css={legalCss}>
		<FormattedMessage
			{...message}
			values={{
				customerAgreement: CustomerAgreementLink,
				privacyPolicy: PrivacyPolicyLink,
				cancelAnytime: CancelAnytimeLink,
			}}
		/>
	</p>
);
